// Agent Profile — the "dive in" behind an Agent Hub row: improvement
// trend, caller DISC mix with best/worst styles, and recent transcripts.
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Loader2, ArrowLeft, TrendingUp, TrendingDown, Phone, Fingerprint, User,
  GraduationCap, Send,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DISC_COLORS = { D: 'bg-rose-500', I: 'bg-amber-400', S: 'bg-emerald-500', C: 'bg-sky-500' };
const DISC_NAMES = { D: 'Dominant', I: 'Influential', S: 'Steady', C: 'Conscientious' };

const Stat = ({ label, value, tone = 'text-slate-900' }) => (
  <div className="bg-white border border-slate-200 rounded-lg px-4 py-3 text-center">
    <div className={`text-xl font-bold tabular-nums ${tone}`}>{value ?? '—'}</div>
    <div className="text-[10px] uppercase tracking-wide text-slate-400">{label}</div>
  </div>
);

const TrendChart = ({ trend }) => {
  if (!trend?.length) return <p className="text-xs text-slate-400 italic">Not enough graded calls for a trend yet.</p>;
  const max = Math.max(...trend.map((t) => t.avg_score), 100);
  return (
    <div className="flex items-end gap-2 h-32" data-testid="agent-trend-chart">
      {trend.map((t) => (
        <div key={t.week} className="flex-1 flex flex-col items-center gap-1 min-w-0">
          <span className="text-[10px] font-bold text-slate-700 tabular-nums">{t.avg_score}</span>
          <div
            className={`w-full rounded-t ${t.avg_score >= 70 ? 'bg-emerald-400' : t.avg_score >= 60 ? 'bg-amber-400' : 'bg-rose-400'}`}
            style={{ height: `${Math.max((t.avg_score / max) * 100, 4)}%` }}
            title={`${t.week}: avg ${t.avg_score} over ${t.calls} calls`}
          />
          <span className="text-[9px] text-slate-400 truncate w-full text-center">{t.week.slice(5)}</span>
        </div>
      ))}
    </div>
  );
};

// One-click training push to THIS agent, pre-filled with their weakest
// DISC style (editable before sending).
const PushToAgentCard = ({ targetUser, worstStyle }) => {
  const { token } = useAuth();
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const styleName = worstStyle ? DISC_NAMES[worstStyle] : null;
  const [title, setTitle] = useState(
    styleName ? `Coaching focus: ${styleName} (${worstStyle}) callers` : 'Coaching focus from your supervisor');
  const [note, setNote] = useState(
    styleName
      ? `Your scores dip with ${styleName}-style callers — review the ${styleName} approach, Do/Don't and objection scripts in DISC Insights before your next shift.`
      : 'Please review the training material your supervisor flagged for you.');

  const send = async () => {
    setSending(true);
    try {
      await axios.post(`${API}/supervisor/push-training`,
        { user_ids: [targetUser.id], all_agents: false, title, note, link: '/disc-insights' },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Training pushed to ${targetUser.name}`);
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Push failed');
    }
    setSending(false);
  };

  return (
    <div className="shrink-0">
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-1.5 bg-teal-600 hover:bg-teal-700 text-white text-xs font-semibold rounded-lg px-3 py-2"
          data-testid="agent-push-training-btn"
        >
          <GraduationCap className="w-4 h-4" /> Push training to {targetUser.name?.split(' ')[0] || 'agent'}
        </button>
      )}
      {open && (
        <div className="bg-white border border-teal-200 rounded-xl p-3 w-80 shadow-sm" data-testid="agent-push-form">
          <p className="text-[10px] font-bold uppercase tracking-widest text-teal-700 mb-1.5">
            Push to {targetUser.name}{styleName ? ` — pre-filled: weakest style ${worstStyle}` : ''}
          </p>
          <input value={title} onChange={(e) => setTitle(e.target.value)}
            className="w-full text-xs border border-slate-200 rounded-lg px-2.5 py-1.5 mb-1.5" data-testid="agent-push-title" />
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3}
            className="w-full text-xs border border-slate-200 rounded-lg px-2.5 py-1.5 mb-2" data-testid="agent-push-note" />
          <div className="flex gap-2">
            <button onClick={send} disabled={sending || !title.trim()}
              className="flex-1 inline-flex items-center justify-center gap-1.5 bg-teal-600 hover:bg-teal-700 text-white text-xs font-semibold rounded-lg px-3 py-1.5 disabled:opacity-50"
              data-testid="agent-push-send">
              {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Send
            </button>
            <button onClick={() => setOpen(false)} className="text-xs text-slate-500 border border-slate-200 rounded-lg px-3 py-1.5">Cancel</button>
          </div>
          <p className="text-[10px] text-slate-400 mt-1.5">Links to DISC Insights · lands in their notification bell · tracked in push receipts.</p>
        </div>
      )}
    </div>
  );
};

export default function AgentProfilePage() {
  const { agentId } = useParams();
  const { token } = useAuth();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/supervisor/agent-profile/${encodeURIComponent(agentId)}?days=${days}`,
        { headers: { Authorization: `Bearer ${token}` } });
      setData(r.data);
    } catch {
      setData(null);
    }
    setLoading(false);
  }, [agentId, days, token]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div className="flex items-center gap-2 text-slate-500 py-20 justify-center"><Loader2 className="w-5 h-5 animate-spin" /> Loading agent profile…</div>;
  }
  if (!data?.found) {
    return (
      <div className="text-center py-16 text-slate-500" data-testid="agent-profile-empty">
        No graded calls found for this agent in the last 8 weeks.
        <div className="mt-3"><Link to="/manager/ops" className="text-teal-700 font-semibold text-sm">← Back to Agent Hub</Link></div>
      </div>
    );
  }

  const s = data.stats || {};
  const totalDisc = (data.disc_mix || []).reduce((a, d) => a + d.calls, 0);

  return (
    <div className="max-w-5xl" data-testid="agent-profile-page">
      <Link to="/manager/ops" className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-slate-800 mb-3" data-testid="agent-profile-back">
        <ArrowLeft className="w-3.5 h-3.5" /> Agent Hub
      </Link>
      <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
        <div className="flex items-center gap-3">
          <span className="w-12 h-12 rounded-full bg-teal-600 text-white font-black text-lg flex items-center justify-center">
            <User className="w-6 h-6" />
          </span>
          <div>
            <h1 className="text-2xl font-bold text-slate-900" data-testid="agent-profile-name">{data.name}</h1>
            <p className="text-xs text-slate-500">
              Ext {data.agent_id}
              {data.improving_pts != null && (
                <span className={`ml-2 inline-flex items-center gap-1 font-bold ${data.improving_pts >= 0 ? 'text-emerald-600' : 'text-rose-600'}`} data-testid="agent-improving">
                  {data.improving_pts >= 0 ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
                  {data.improving_pts >= 0 ? '+' : ''}{data.improving_pts} pts over 8 weeks
                </span>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {data.target_user && (
            <PushToAgentCard targetUser={data.target_user} worstStyle={data.worst_style} />
          )}
          <div className="flex gap-1.5">
            {[30, 60, 90].map((d) => (
              <button key={d} onClick={() => setDays(d)}
                className={`text-xs font-semibold rounded-lg px-3 py-1.5 border ${days === d ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
                data-testid={`agent-days-${d}`}>
                {d}d
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-5">
        <Stat label={`Graded (${data.days}d)`} value={s.graded_calls} />
        <Stat label="Avg score" value={s.avg_score} tone={s.avg_score >= 70 ? 'text-emerald-600' : s.avg_score >= 60 ? 'text-amber-600' : 'text-rose-600'} />
        <Stat label="Best" value={s.best_score} tone="text-emerald-600" />
        <Stat label="Worst" value={s.worst_score} tone="text-rose-600" />
        <Stat label="Low (<60)" value={s.low_calls} tone={s.low_calls > 0 ? 'text-rose-600' : 'text-slate-900'} />
      </div>

      <div className="grid lg:grid-cols-2 gap-4 mb-5">
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <p className="text-sm font-bold text-slate-900 mb-3">Improvement trend — weekly avg score</p>
          <TrendChart trend={data.trend} />
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="agent-disc-mix">
          <p className="text-sm font-bold text-slate-900 flex items-center gap-1.5 mb-3">
            <Fingerprint className="w-4 h-4 text-teal-600" /> Caller DISC mix &amp; where they score
          </p>
          {totalDisc === 0 && <p className="text-xs text-slate-400 italic">No DISC-profiled calls in this window.</p>}
          {totalDisc > 0 && (
            <div className="space-y-2">
              {(data.disc_mix || []).map((d) => (
                <div key={d.style} className="flex items-center gap-2">
                  <span className={`w-6 h-6 rounded-full ${DISC_COLORS[d.style]} text-white text-[11px] font-black flex items-center justify-center shrink-0`}>{d.style}</span>
                  <div className="flex-1 bg-slate-100 rounded-full h-2.5 overflow-hidden">
                    <div className={DISC_COLORS[d.style]} style={{ width: `${totalDisc ? Math.max((d.calls / totalDisc) * 100, 2) : 0}%`, height: '100%' }} />
                  </div>
                  <span className="text-[11px] text-slate-500 w-32 shrink-0 text-right tabular-nums">
                    {d.calls} calls · avg {d.avg_score ?? '—'}
                  </span>
                </div>
              ))}
              {(data.best_style || data.worst_style) && (
                <p className="text-[11px] text-slate-600 pt-1" data-testid="agent-style-verdict">
                  {data.best_style && <>Strongest with <span className="font-bold text-emerald-700">{DISC_NAMES[data.best_style]} ({data.best_style})</span> callers.</>}
                  {data.worst_style && data.worst_style !== data.best_style && (
                    <> Needs coaching for <span className="font-bold text-rose-700">{DISC_NAMES[data.worst_style]} ({data.worst_style})</span> — see <Link to="/disc-insights" className="underline text-teal-700">DISC Insights</Link>.</>
                  )}
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="agent-recent-calls">
        <div className="px-4 py-3 border-b border-slate-100">
          <p className="text-sm font-bold text-slate-900">Recent transcripts</p>
          <p className="text-[11px] text-slate-400">Last {data.recent_calls?.length || 0} graded calls — click through to the full transcript &amp; playbook in the Coaching Lab.</p>
        </div>
        <div className="divide-y divide-slate-100">
          {(data.recent_calls || []).map((c) => (
            <Link key={c.call_id} to={`/calls/${c.call_id}`} className="flex items-start gap-3 px-4 py-3 hover:bg-slate-50" data-testid={`agent-call-${c.call_id}`}>
              <span className={`text-xs font-bold rounded-full px-2 py-0.5 shrink-0 mt-0.5 ${c.score >= 70 ? 'bg-emerald-100 text-emerald-700' : c.score >= 60 ? 'bg-amber-100 text-amber-700' : 'bg-rose-100 text-rose-700'}`}>
                {c.score}
              </span>
              <div className="min-w-0">
                <p className="text-xs font-semibold text-slate-800 flex items-center gap-1.5">
                  <Phone className="w-3 h-3 text-slate-400" /> {c.customer || 'Unknown caller'}
                  <span className="text-slate-400 font-normal">· {String(c.call_date || '').slice(0, 10)}{c.duration_seconds ? ` · ${Math.round(c.duration_seconds / 60)}min` : ''}</span>
                </p>
                {c.snippet && <p className="text-[11px] text-slate-500 mt-0.5 line-clamp-2">{c.snippet}</p>}
              </div>
            </Link>
          ))}
          {(!data.recent_calls || data.recent_calls.length === 0) && (
            <p className="px-4 py-6 text-xs text-slate-400 italic">No graded calls in this window.</p>
          )}
        </div>
      </div>
    </div>
  );
}
